import React, { useState } from 'react';
import { AlertTriangle, Check, X } from 'lucide-react';
import {
  EMPTY_POKA_YOKE,
  TPL_POKA_YOKE_QUESTIONS,
  validateTplPokaYoke,
  type TplPokaYokeAnswers,
} from '../../services/tplPokaYoke';

interface TplPokaYokeDialogProps {
  open: boolean;
  activityTitle: string;
  onCancel: () => void;
  onConfirm: (answers: TplPokaYokeAnswers, force: boolean) => void;
}

export const TplPokaYokeDialog: React.FC<TplPokaYokeDialogProps> = ({
  open,
  activityTitle,
  onCancel,
  onConfirm,
}) => {
  const [answers, setAnswers] = useState<TplPokaYokeAnswers>({ ...EMPTY_POKA_YOKE });

  if (!open) return null;

  const result = validateTplPokaYoke(answers);

  const toggle = (key: keyof TplPokaYokeAnswers) =>
    setAnswers((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-black/45" aria-label="Fechar" onClick={onCancel} />
      <div className="relative w-full max-w-lg rounded-lg border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-700 dark:bg-[var(--bg-surface)]">
        <h3 className="text-[14px] font-bold text-slate-900 dark:text-slate-100">Poka-Yoke — Validação do TPL</h3>
        <p className="mt-1 text-[11px] text-slate-500">Atividade: <strong>{activityTitle}</strong></p>

        <div className="mt-4 space-y-3">
          {TPL_POKA_YOKE_QUESTIONS.map((q) => (
            <label key={q.key} className="flex cursor-pointer items-start gap-2 rounded-md border border-slate-100 p-2.5 hover:bg-slate-50 dark:border-slate-800">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-blue-600"
                checked={answers[q.key]}
                onChange={() => toggle(q.key)}
              />
              <span>
                <span className="block text-[12px] font-medium text-slate-800 dark:text-slate-200">{q.label}</span>
                <span className="block text-[10px] text-slate-400">{q.hint}</span>
              </span>
            </label>
          ))}
        </div>

        {!result.passed && (
          <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-[11px] text-amber-900">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <p>{result.warningMessage}</p>
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-[11px] text-slate-500 hover:bg-slate-100">
            <X size={14} /> Cancelar
          </button>
          {result.passed ? (
            <button
              type="button"
              onClick={() => onConfirm(answers, false)}
              className="inline-flex items-center gap-1 rounded-md bg-[#2F6FED] px-3 py-1.5 text-[12px] font-semibold text-white"
            >
              <Check size={14} /> Confirmar e salvar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onConfirm(answers, true)}
              className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-[12px] font-semibold text-amber-900"
            >
              Salvar mesmo assim
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TplPokaYokeDialog;
